import React from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import type { MetricRecord } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"
import { cn } from "@/utils/cn"

import { AUDIO_OBSERVATION_HIGHLIGHT_COLOR } from "../audio-metric-constants"
import { parseResultJson } from "../helpers/parseResultJson"

interface TimestampRegion {
  start_time: number
  end_time: number
  label: string
}

export function formatTimeToMinSec(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TypeScript mixin constructors require any[] rest params
type Constructor<T = object> = new (...args: any[]) => T

/**
 * Mixin that provides shared audio regions + observations rendering for LLM
 * audio metrics (Binary, Categorical, Numerical).
 *
 * Usage (compose on top of the LLM class + audio gate):
 *   class AudioBinaryLLMMetricRecord
 *     extends WithLLMAudioRegionsMixin(WithAudioGateMixin(BinaryLLMMetricRecord)) { ... }
 *
 * Each audio LLM metric's _renderDeepDive then becomes:
 *   _renderDeepDive(mo, testCase) {
 *     return (
 *       <div className="space-y-8">
 *         {super._renderDeepDive(mo, testCase)}
 *         {this._renderAudioObservationsSection(mo)}
 *       </div>
 *     );
 *   }
 */
export function WithLLMAudioRegionsMixin<T extends Constructor<MetricRecord>>(
  Base: T,
) {
  return class extends Base {
    getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
      if (mo?.error_message && mo?.error_message !== "") {
        return []
      }
      try {
        // Use subvalues_by_timestamp for waveform regions (preferred source)
        const observations = mo.subvalues_by_timestamp
        if (observations && observations.length > 0) {
          return observations.map((obs, idx) => ({
            id: `audio-obs:${idx}`,
            // @deprecated fallback: obs.timestamp — remove after migration to start_offset/end_offset
            start: obs.start_offset ?? obs.timestamp ?? 0,
            end: obs.end_offset ?? (obs.timestamp ?? 0) + 0.5,
            color:
              this.getAudioRegionColorForVerdict(obs.string_value ?? null) ??
              AUDIO_OBSERVATION_HIGHLIGHT_COLOR,
            drag: false,
            resize: false,
          }))
        }

        // @deprecated fallback: result_json.timestamps — remove after subvalues_by_timestamp migration is complete
        const timestamps: TimestampRegion[] =
          parseResultJson<{ timestamps?: TimestampRegion[] }>(mo.result_json)
            ?.timestamps || []
        if (timestamps.length > 0) {
          return timestamps.map((ts, idx) => ({
            id: `audio-obs:${idx}`,
            start: ts.start_time,
            end: ts.end_time,
            color: AUDIO_OBSERVATION_HIGHLIGHT_COLOR,
            drag: false,
            resize: false,
          }))
        }

        return []
      } catch {
        return []
      }
    }

    _renderAudioObservationsSection(
      mo: RunMetricOutput,
    ): React.ReactNode | null {
      const observations = mo.subvalues_by_timestamp || []

      if (observations.length === 0) {
        return null
      }

      const handleSeekToTimestamp = (timestamp: number) => {
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
          time: timestamp,
        })
      }

      return (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">
            Audio Observations ({observations.length})
          </h3>

          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {observations.map((obs: SubvalueByTimestamp, idx: number) => {
              // @deprecated fallback: obs.timestamp — remove after migration to start_offset
              const startOffset = obs.start_offset ?? obs.timestamp ?? 0
              // @deprecated fallback: obs.metadata?.timestamp_mm_ss — remove after migration to start_offset
              const timestampLabel =
                (obs.metadata?.timestamp_mm_ss as string | undefined) ||
                formatTimeToMinSec(startOffset)
              // Fall back to string_value for legacy rows where it held the description.
              const observationText =
                (obs.metadata?.explanation as string | undefined) ||
                obs.string_value ||
                (obs.metadata?.observation as string | undefined) ||
                "No observation text"
              return (
                <div
                  key={idx}
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg border",
                    "border-coval-info-border bg-coval-info-surface",
                    "cursor-pointer",
                    "transition-colors duration-150",
                  )}
                  onClick={() => handleSeekToTimestamp(startOffset)}
                >
                  <div className="shrink-0">
                    <div
                      className={cn(
                        "px-2 py-1 rounded text-xs font-mono",
                        "bg-coval-info-surface text-coval-info-on-surface",
                      )}
                    >
                      {timestampLabel}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">{observationText}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    }
  }
}
